import {BettingTypesMap} from "../../features/core/definitions";

export type NormalizedLeg = {
    key: string;
    card: string;
    cardId: any;
    raceNo: number;
    betType: number;
    /** Bacağın kupondaki sırası - "1. Ayak" / "2. Ayak" başlıkları buradan. */
    index: number;
    columnLabel: string;
    selections: string[];
    /** Soneki soyulmamış ham seçim; oran satırıyla eşleşmede kullanılıyor. */
    rawSelections: string[];
    isCombine: boolean;
    /** Yalnız `rows`tan gelir: sunucunun yazdığı hipodrom adı (haritaya gerek kalmadan). */
    hippodromeLabel?: string;
};

const stripLegSuffix = (v: any): string =>
    String(v ?? '').replaceAll('EX', '').replaceAll('H2H', '')
        .replaceAll('T2', '').replaceAll('T3', '').replaceAll('T4', '').replaceAll('WW', '');

const stripPairJoinSuffix = (v: any): string =>
    String(v ?? '').replaceAll('EX', '').replaceAll('H2H', '');

export function getTicketClassification(betType: any) {
    const info = BettingTypesMap[Number(betType)];
    const columnLabel = info?.columnLabel;
    const isH2H = !!info?.isHeadToHead;
    return {
        info,
        isCombine: !!info?.isCombine,
        isLongterm: !!info?.isLongterm,
        isFob: !!info?.isFob,
        isH2H,
        columnLabel,
        // Kim Geçer'in columnLabel'ı 'Koşu' olsa da İkili gibi TEK koşudaki at çiftidir.
        isPairColumn: columnLabel === "At" || isH2H,
    };
}

/** "DDMMYYYY-keyword..." -> "YYYY-MM-DD" */
export const parseCardDate = (card: string): string => {
    const digits = String(card).split("-")[0];
    return `${digits.substring(4, 8)}-${digits.substring(2, 4)}-${digits.substring(0, 2)}`;
};

/** "YYYY-MM-DD" -> "DDMMYYYY" (program servisinin beklediği biçim) */
export const toProgramDate = (date: string): string => {
    const [y, m, d] = date.split("-");
    return `${d}${m}${y}`;
};

export function rowHippodromeKeyword(card: string): string {
    return String(card).split("-")[1]?.toLowerCase() ?? '';
}

function normalizeFromRows(ticket: any): NormalizedLeg[] {
    const {isCombine, isPairColumn, isH2H, columnLabel} = getTicketClassification(ticket.bet_type);
    const rows: any[] = Array.isArray(ticket.rows) ? ticket.rows : [];
    const isPairSplit = !isCombine && isPairColumn && !isH2H;

    return rows.flatMap((row: any, index: number): NormalizedLeg[] => {
        // Tek bir satırın bozukluğu (ör. `odd_key` yok) BÜTÜN biletin detayını
        // çökertmesin diye o satır atlanıyor.
        if (row?.odd_key == null) {
            console.warn(`[TicketResolve] ${ticket.id} biletinin ${index}. satırında odd_key yok, atlanıyor`, row);
            return [];
        }
        const rawValue = String(row.odd_key);
        const rawParts = rawValue.includes('-')
            ? rawValue.replaceAll('EX', '').split('-').map(stripPairJoinSuffix)
            : [rawValue];
        const selections = rawParts.map(stripLegSuffix);
        const rowColumnLabel = BettingTypesMap[Number(row.bet_type)]?.columnLabel || columnLabel;

        if (isPairSplit && selections.length === 2) {
            return [0, 1].map((pairIdx): NormalizedLeg => ({
                key: `row-${ticket.id}-${index}-${pairIdx}`,
                card: row.card,
                cardId: row.card_id,
                raceNo: Number(row.race_id),
                betType: Number(row.bet_type),
                index: pairIdx,
                columnLabel: rowColumnLabel,
                selections: [selections[pairIdx]],
                rawSelections: [rawParts[pairIdx]],
                isCombine: false,
            }));
        }

        return [{
            key: `row-${ticket.id}-${index}`,
            card: row.card,
            cardId: row.card_id,
            raceNo: Number(row.race_id),
            betType: Number(row.bet_type),
            index,
            columnLabel: rowColumnLabel,
            selections,
            rawSelections: rawParts,
            isCombine,
            hippodromeLabel: row.hippodrome_name,
        }];
    });
}

function normalizeFromLegs(ticket: any): NormalizedLeg[] {
    const {isCombine, isPairColumn, isH2H, columnLabel} = getTicketClassification(ticket.bet_type);
    const legs: any[] = Array.isArray(ticket.legs) ? ticket.legs : [];

    const isKeyed = (leg: any) => leg !== null && typeof leg === 'object' && !Array.isArray(leg);

    if (legs.some(isKeyed)) {
        return legs.flatMap((leg: any, index: number): NormalizedLeg[] => {
            const legKey = Object.keys(leg || {})[0];
            const value = legKey ? leg[legKey] : null;
            if (!legKey || value == null) {
                console.warn(`[TicketResolve] ${ticket.id} biletinin ${index}. bacağı beklenmedik biçimde, atlanıyor`, leg);
                return [];
            }
            // Anahtar "<card>_<koşu>_<tip>" biçiminde OLMAYABİLİR (eski biçim):
            // o zaman kart ve koşu biletin kendi alanlarından alınıyor.
            const parts = legKey.split('_');
            const hasKeyParts = parts.length >= 2;
            const legCard = hasKeyParts ? parts[0] : ticket.card;
            const legRunNo = hasKeyParts ? Number(parts[1]) : Number(ticket.race_id);
            const legBetType = hasKeyParts && parts[2] != null ? Number(parts[2]) : Number(ticket.bet_type);

            const rawValue = String(Array.isArray(value) ? value[0] : value);
            const rawParts = rawValue.includes('-')
                ? rawValue.replaceAll('EX', '').replaceAll('H2H', '').split('-').map(stripPairJoinSuffix)
                : [rawValue];

            return [{
                key: `keyed-${ticket.id}-${index}`,
                card: legCard,
                cardId: hasKeyParts ? null : ticket.card_id,
                raceNo: legRunNo,
                betType: legBetType,
                index,
                columnLabel: BettingTypesMap[legBetType]?.columnLabel || columnLabel,
                selections: rawParts.map(stripLegSuffix),
                rawSelections: rawParts,
                isCombine: isCombine || hasKeyParts,
            }];
        });
    }

    if (isH2H) {
        const flatRaw = legs.flat().map((h: any) => String(h));
        const pairs: string[][] = [];
        for (let i = 0; i < flatRaw.length; i += 2) {
            pairs.push(flatRaw.slice(i, i + 2));
        }
        return pairs.map((pairRaw: string[], index: number): NormalizedLeg => ({
            key: `leg-${ticket.id}-h2h-${index}`,
            card: ticket.card,
            cardId: ticket.card_id,
            raceNo: Number(ticket.race_id),
            betType: Number(ticket.bet_type),
            index,
            columnLabel,
            selections: pairRaw.map(stripLegSuffix),
            rawSelections: pairRaw,
            isCombine: false,
        }));
    }

    return legs.map((leg: any, index: number): NormalizedLeg => {
        // (b) ESKİ biçim: bacak dizi değil, tek skaler.
        const horses: any[] = Array.isArray(leg) ? leg : [leg];
        const raceNo = isPairColumn ? Number(ticket.race_id) : Number(ticket.race_id) + index;
        const rawParts = horses.map((h: any) => String(h));
        return {
            key: `leg-${ticket.id}-${index}`,
            card: ticket.card,
            cardId: ticket.card_id,
            raceNo,
            betType: Number(ticket.bet_type),
            index,
            columnLabel,
            selections: rawParts.map((h: string) => (Number(h) === 32 ? '32' : stripLegSuffix(h))),
            rawSelections: rawParts,
            isCombine: false,
        };
    });
}

export function normalizeTicketLegs(ticket: any): NormalizedLeg[] {
    if (Array.isArray(ticket?.rows) && ticket.rows.length > 0) {
        return normalizeFromRows(ticket);
    }
    if (Array.isArray(ticket?.legs) && ticket.legs.length > 0) {
        return normalizeFromLegs(ticket);
    }
    return [];
}
